// @school-apps/core/lib/dates — the hub's date-only calendar helpers (P4 X·3; previously copied
// in both apps). `due` dates are date-only ISO strings (YYYY-MM-DD): a due date is a calendar
// date, so local-day comparison is honest and string compare is date compare. This module is
// deliberately PURE + BROWSER-SAFE (no node imports) — the app bundles import it directly via
// the "@school-apps/core/lib/dates" subpath export.

/** The user's LOCAL calendar day as YYYY-MM-DD (due dates are local days, not UTC instants). */
export function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * The local calendar day n days from now, as YYYY-MM-DD. CALENDAR days, not n × 24h (DST·1): a span
 * that crosses a DST change is an hour short or long, so adding milliseconds landed a day off for
 * one hour a night (00:00–00:59 before the fall-back, 23:00–23:59 before the spring-forward).
 * `setDate` steps the local date and leaves the wall-clock time alone.
 */
export const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return localDateStr(d);
};

/** Sort comparator on the date-only `due` field (lexicographic IS chronological here). */
export const byDue = (a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : 0);

/** A comparison key: a string as itself, anything else (absent, number, object) as "" — sorts oldest. */
const key = (v) => (typeof v === "string" ? v : "");

/**
 * BIG·1b — newest-created first, as a TOTAL order: `createdAt` descending, then `id` descending.
 * Ids are unique, so nothing can tie past the second step, and 0 comes back ONLY for genuine
 * identity (the same record compared with itself).
 *
 * It replaces this, which shipped in all three client bundles:
 *   (a, b) => ((a.createdAt ?? "") < (b.createdAt ?? "") ? 1 : -1)
 * Two faults, not one. (1) Same-millisecond `createdAt` values tie, and a Canvas import creates
 * courses in a LOOP, so same-millisecond is the normal case there, not an edge. (2) It never
 * returns 0, so it is not a consistent comparator at all — `Array#sort` is entitled to produce
 * arbitrary orderings from one, not merely unstable ones, and the answer could change with a V8
 * release. The practical symptom is that the result depended on the order the records arrived in.
 *
 * `createdAt` is canonical ISO from `now()`, so lexical compare IS chronological (the same
 * assumption last-write-wins rests on). An absent or malformed `createdAt` sorts OLDEST — a record
 * that never said when it was made cannot outrank one that did.
 */
export const byCreatedDesc = (a, b) => {
  const ac = key(a?.createdAt), bc = key(b?.createdAt);
  if (ac !== bc) return ac < bc ? 1 : -1;
  const ai = key(a?.id), bi = key(b?.id);
  return ai < bi ? 1 : ai > bi ? -1 : 0;
};

/**
 * The term the hub opens on: the newest-created course's term, or null when there are no courses.
 * ONE owner for the pick, imported by all three apps — the same "one definition, provable lockstep"
 * rule the image screening follows. Because `byCreatedDesc` is a total order, the answer does not
 * depend on the order the courses arrived in.
 * @param {Array<{ id?: string, term?: string, createdAt?: string }>} courses
 * @returns {string|null}
 */
export function latestTerm(courses) {
  const live = (Array.isArray(courses) ? courses : []).filter((c) => c && typeof c === "object");
  if (live.length === 0) return null;
  return [...live].sort(byCreatedDesc)[0].term ?? null;
}

const DUE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Server-side validation: a well-formed, parseable date-only ISO string. */
export const isDue = (v) => typeof v === "string" && DUE_RE.test(v) && !Number.isNaN(Date.parse(v));
